
import React, {
  FC,
  useEffect,
  useReducer,
  useRef,
  useContext,
  useState,
  useLayoutEffect,
  memo,
  useMemo,
  useCallback,
} from "react";
import Select, {
  GroupBase,
  OptionsOrGroups,
  PropsValue,
  SingleValue,
} from "react-select";
import { useLocation } from "react-router-dom";

import useFetch from "src/hooks/useFetch";
import { LoaderContext } from "src/context/loaderContext";
import { AuthContext } from "src/context/authContext";

import {
  UserListApiResponse,
} from "src/components/authSubHeader/types";
import {
  ActionDashboardProps,
  ActionStatisticsDashboardActionType,
  PatientFilterState,
  PatientFilterStateActions,
  TodoResponseType,
} from "src/pages/actionDashboard/types";
import { tableFilterReducer } from "src/pages/actionDashboard/reducer";
import FooterText from "../../footerText";
import { isZilrettaUser, zilrettaDrugSelection } from "src/helpers";

const ActionDashboard: FC<ActionDashboardProps> = ({
  apiEndpoint,
  userListEndpoint,
  initialState,
  tabs,
  FilterComponent,
  allowedRoles,
}) => {
  const [queryParams, dispatch] = useReducer(tableFilterReducer, initialState);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const { userData, isManufacturer } = useContext(AuthContext);
  const location = useLocation();
  const [userChange, setUserChange] = useState<PropsValue<number>>({
    label: userData?.name ?? "",
    value: userData?.id ?? 0,
  } as unknown as PropsValue<number>);
  const firstUpdate = useRef(true);

   const params = {
              ...queryParams,
              drug_id: isZilrettaUser(userData) ? "zilretta" : queryParams.drug_id
          };
  const { data: dashboardData, error: dashboardError, mutate: updateData } =
    useFetch<TodoResponseType>(apiEndpoint, params, {}, true);

  const { data: userListData, mutate: userListMutate, error: userListError } =
    useFetch<UserListApiResponse>(userListEndpoint, {}, {}, false);

  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [isPaginationUpdated, updatePaginationChange] = useState<boolean>(false)

  // Show loader initially
  useLayoutEffect(() => {
    setLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // initialize user role
  useEffect(() => {
    if (userData) {
      dispatch({
        type: PatientFilterStateActions.CHANGE_USER_ROLE_AND_ID,
        payload: {
          role: userData.ref_type,
          id: userData.id.toString(),
          user_ref: "",
        },
      });
    }
  }, [userData]);



  const hasArrayData = (data: any): boolean => {
    if (!data) return false;

    // case 1: agar data direct array hai
    if (Array.isArray(data)) {
      return data.length > 0;
    }

    // case 2: agar data object hai jiske andar arrays hain
    if (typeof data === "object") {
      return Object.values(data).some(
        (val) => Array.isArray(val) && val.length > 0
      );
    }

    return false;
  };


  const getDefaultActiveTab = (dashboardData: any) => {
    if (!dashboardData?.data) return tabs[0].key;

    const filledTabs = tabs.filter((tab) => {
      const data = tab.dataSelector(dashboardData.data);
      return hasArrayData(data); // 👈 safe check
    });

    if (filledTabs.length === 0) {
      return "coverage_available";
    }

    if (filledTabs.length === tabs.length) {
      return "coverage_available";
    }

    return filledTabs[0].key;
  };
  // 👇 jab data load ho jaye tab default tab set karo
  useEffect(() => {
    if (dashboardData || dashboardError) {
      hideLoader()
      if (dashboardData && dashboardData.data) {
        const defaultTab = getDefaultActiveTab(dashboardData);
        // sirf tabhi update karo jab current tab alag ho
        if (isPaginationUpdated) {
          updatePaginationChange(false)
        } else if (activeTab !== defaultTab) {
          setActiveTab(defaultTab);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData, dashboardError]);

  const isAllowedUser =
    userData?.ref_type && allowedRoles.includes(userData.ref_type);
  const isAllowedPage =
    !location.pathname.includes("createuser") &&
    !location.pathname.includes("updateuser") &&
    !location.pathname.includes("eobs");

  const handleUserChange = (value: SingleValue<number>) => {
    setUserChange(value);
    const { value: selectedId, role } = value as unknown as {
      label: string;
      value: number;
      role: string;
    };
    const queries = structuredClone(params)

    queries.role = role;
    queries.id = String(selectedId);
    queries.ref_user = String(selectedId);


    setLoader();
    updateData({}, {}, queries);
  };

  // setup default user selection
  useEffect(() => {
    if (isManufacturer) {
      if (firstUpdate.current && userData?.ref_type !== "rep" && isAllowedPage) {
        firstUpdate.current = false;
        userListMutate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userListData?.code || userListError) hideLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userListData, userListError]);

  const onPageChanged = (pageObj: { selected: number }, changeAction: any) => {
    const action: ActionStatisticsDashboardActionType = {
      type: changeAction,
      payload: pageObj.selected + 1,
    };

    updatePaginationChange(true)
    const newState = tableFilterReducer(params, action);
    dispatch(action);
    setLoader();
    updateData({}, {}, newState);
  };
  const activeTabObj = tabs.find((t) => t.key === activeTab)!;

  const handlePageChange = useCallback(
    (pageObj: { selected: number }) => onPageChanged(pageObj, activeTabObj.changeAction),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTabObj.changeAction, params]
  );

  const refreshListing = useCallback(
    () => updateData({}, {}, params),
    [updateData, queryParams]
  );

  // active tab object

  // memoized table data for active tab
  const tableData = useMemo(
    () => activeTabObj.dataSelector(dashboardData?.data ?? {}),
    [dashboardData, activeTabObj]
  );

  const updateQueryParamsAndCallAPI = (updatedQueries: PatientFilterState): void => {

    //console.log("updatedQueries....",updatedQueries)
    const params = {
              ...updatedQueries,
              drug_id: isZilrettaUser(userData) ? "zilretta" :  updatedQueries.drug_id === "All Product" ? '' : updatedQueries.drug_id
          };
    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,
      payload: params,
    });
    setLoader();
    updateData({}, {}, params)
  };

  return (
    <section className="overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill action-dashboard">
      <div className="container-fluid pt-3 d-flex flex-column flex-fill">
        {/* Header */}
        <div className="d-flex align-items-center mb-3 flex-wrap gap-4">
          <h2 className="m-0 page-heading">Action Dashboard</h2>
          <div className="user-filter">
            {isAllowedUser && isAllowedPage && (
              <Select
                value={userChange}
                options={
                  (userListData?.data ?? []).map((u) => ({
                    value: u.id,
                    label: u.fullname,
                    role: u.role,
                  })) as unknown as OptionsOrGroups<number, GroupBase<number>>
                }
                isSearchable={true}
                noOptionsMessage={() => "Select Option..."}
                onChange={handleUserChange}
              />
            )}
          </div>
        </div>

        {/* Filters */}
        <FilterComponent
          doctorDropdown={dashboardData?.data?.doctor_dropdown ?? []}
          practiceDropdown={dashboardData?.data?.practice_dropdown ?? []}
          defaultQueryParams={params}
          updateQueryParams={

            //   (queries: PatientFilterState) => {
            //   setLoader();
            //   Object.assign(queryParams, queries);
            //   updateData({}, {}, queryParams);
            // }
            updateQueryParamsAndCallAPI
          }
        />

        {/* Tabs */}
        <div className="nav nav-pills mb-3 mt-2 gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Active Tab Content */}
        <div className="tab-content d-flex flex-column flex-fill">
          <div className="tab-pane show active fade d-flex flex-column flex-fill">
            <p className="small">{activeTabObj.description}</p>
            <div className="card mb-3 d-flex flex-fill">
              <div className="card-body p-0 d-flex flex-column">
                <activeTabObj.Component
                  tableData={tableData}
                  currentPage={queryParams[activeTabObj.pageKey]}
                  onPageChange={handlePageChange}
                  refreshListing={refreshListing}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <FooterText footerType="black" />
    </section>
  );
};

export default memo(ActionDashboard);
